/**
 * Chutt... - Brand Creation Wizard
 * Four-step wizard: Brand info → Social URLs → Missions → Forfait
 */
import React, { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  IconButton,
  LinearProgress,
  Collapse,
  Switch,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CheckIcon from '@mui/icons-material/Check';
import BlockIcon from '@mui/icons-material/Block';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

import { monitoringAPI, objectivesAPI, missionsAPI, agencyAPI } from '../../services/api';
import { comprendreAPI } from '../../services/apiV2';
import { colors } from '../../styles/colors';
import TargetProteger from './TargetProteger';
import TargetConfiance from './TargetConfiance';
import TargetRayonner from './TargetRayonner';
import TargetFederer from './TargetFederer';

// Profile types
const PROFILE_TYPES = [
  { id: 'personnalite_publique', name: 'Personnalité Publique', description: 'Anticiper les crises, protéger votre image, gérer les contre-narratifs.' },
  { id: 'artiste', name: 'Artiste / Musicien', description: 'Protéger votre image artistique, gérer la notoriété, suivre le buzz.' },
  { id: 'createur', name: 'Créateur / Influenceur', description: 'Engagement communauté, croissance audience, buzz positif.' },
  { id: 'organisation', name: 'Organisation / Entreprise', description: 'KPIs business, satisfaction client, veille concurrentielle.' },
  { id: 'agence_rp', name: 'Agence RP', description: 'Vue multi-marques, rapports clients, gestion portefeuille.' },
];

// Sectors list (extended)
const SECTORS = [
  { id: 'telecom', name: 'Télécommunications' },
  { id: 'banque_finance', name: 'Banque & Finance' },
  { id: 'tech', name: 'Technologie' },
  { id: 'mines_energie', name: 'Mines & Énergie' },
  { id: 'agriculture', name: 'Agriculture & Agroalimentaire' },
  { id: 'hospitality', name: 'Hôtellerie, Tourisme & Luxe' },
  { id: 'distribution', name: 'Distribution & Retail' },
  { id: 'btp', name: 'BTP & Immobilier' },
  { id: 'transport', name: 'Transport & Logistique' },
  { id: 'sante', name: 'Santé & Pharma' },
  { id: 'media', name: 'Médias & Communication' },
  { id: 'sport', name: 'Sport & Divertissement' },
  { id: 'mode', name: 'Mode & Beauté' },
  { id: 'art', name: 'Art & Culture' },
  { id: 'education', name: 'Éducation' },
  { id: 'politique', name: 'Politique, Droit & ONG' },
  { id: 'autre', name: 'Autre' },
];

const steps = ['Informations', 'Réseaux sociaux', 'Vos missions'];

// Platform display config
const PLATFORMS = [
  { key: 'google_news', label: 'Google News', alwaysAvailable: true },
  { key: 'perplexity', label: 'Perplexity', alwaysAvailable: true },
  { key: 'rss', label: 'RSS secteur', alwaysAvailable: true },
  { key: 'instagram', label: 'Instagram', urlKey: 'instagram_url' },
  { key: 'twitter', label: 'Twitter / X', urlKey: 'twitter_url' },
  { key: 'linkedin', label: 'LinkedIn', urlKey: 'linkedin_url' },
  { key: 'facebook', label: 'Facebook', urlKey: 'facebook_url' },
  { key: 'youtube', label: 'YouTube', urlKey: 'youtube_url' },
  { key: 'tiktok', label: 'TikTok', urlKey: 'tiktok_url' },
];

function BrandWizard({ open, onClose, onBrandCreated }) {
  // Step state
  const [activeStep, setActiveStep] = useState(0);

  // Step 1: Brand info
  const [brandName, setBrandName] = useState('');
  const [keywords, setKeywords] = useState([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [sector, setSector] = useState('autre');
  const [profileType, setProfileType] = useState('organisation');

  // Step 2: Social URLs + Digital Properties
  const [socialUrls, setSocialUrls] = useState({
    instagram_url: '',
    twitter_url: '',
    linkedin_url: '',
    facebook_url: '',
    youtube_url: '',
    tiktok_url: '',
    website_url: '',
    shop_url: '',
    google_maps_url: '',
    tripadvisor_url: '',
    booking_url: '',
    marketplace_url: '',
  });

  // Step 3: Analysis & Missions
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [selectedObjectives, setSelectedObjectives] = useState({}); // eslint-disable-line no-unused-vars
  const [customTargets, setCustomTargets] = useState({}); // eslint-disable-line no-unused-vars
  const [selectedMissions, setSelectedMissions] = useState({});
  const [targetConfigs, setTargetConfigs] = useState({});  // { protect: {...}, trust: {...}, ... }
  const [missionDefs, setMissionDefs] = useState([]);
  const [sectorSources, setSectorSources] = useState(null);

  // Step 4: Forfait (Tier selection)
  const [objectifsEtendus, setObjectifsEtendus] = useState(false);

  // Magic social URL search
  const [searchingSocials, setSearchingSocials] = useState(false);
  const [socialsSearchDone, setSocialsSearchDone] = useState(false);
  const [filledFields, setFilledFields] = useState(new Set());

  // Loading & Error
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  // Scraping state
  const [scraping, setScraping] = useState(false);
  const [scrapingStep, setScrapingStep] = useState({ step: 0, message: '', total: 0 });

  // Handle keyword add
  const handleAddKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput('');
    }
  };

  const handleKeywordKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddKeyword();
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  // Magic social URL search when entering Step 2
  useEffect(() => {
    if (activeStep !== 1 || socialsSearchDone || searchingSocials || !brandName.trim()) return;

    const searchUrls = async () => {
      setSearchingSocials(true);
      try {
        const res = await objectivesAPI.searchSocialUrls(brandName, sector, profileType);
        const urls = res.data?.urls || {};
        if (Object.keys(urls).length > 0) {
          const newFilled = new Set();
          // Stagger the fill animation: each field appears 200ms apart
          const entries = Object.entries(urls);
          for (let i = 0; i < entries.length; i++) {
            const [key, value] = entries[i];
            if (value) {
              await new Promise(r => setTimeout(r, 200));
              setSocialUrls(prev => ({ ...prev, [key]: value }));
              newFilled.add(key);
              setFilledFields(new Set(newFilled));
            }
          }
        }
      } catch (err) {
        console.warn('Social URL search failed (non-blocking):', err);
      } finally {
        setSearchingSocials(false);
        setSocialsSearchDone(true);
      }
    };

    searchUrls();
  }, [activeStep, brandName, sector, profileType, socialsSearchDone, searchingSocials]);

  // Fetch enriched mission definitions when transitioning to step 3
  const fetchMissionDefinitions = useCallback(async () => {
    try {
      const res = await missionsAPI.getDefinitions(profileType, sector);
      const data = res.data || res;
      setMissionDefs(data.missions || []);
      setSectorSources(data.sector_sources || null);

      // Pre-select recommended missions
      const missionSelection = {};
      (data.missions || []).forEach(m => {
        if (m.is_recommended) {
          missionSelection[m.key] = true;
        }
      });
      setSelectedMissions(missionSelection);
    } catch (err) {
      console.warn('Failed to fetch enriched definitions, using defaults:', err);
    }
  }, [profileType, sector]);

  // Step 1 → Step 2: Get recommendations from benchmarks (instant)
  const handleAnalyze = async () => {
    if (!brandName.trim()) {
      setError('Le nom de la marque est requis');
      return;
    }

    const allKeywords = keywords.length > 0 ? keywords : [brandName];
    if (!allKeywords.includes(brandName)) {
      allKeywords.unshift(brandName);
    }

    setAnalyzing(true);
    setError(null);

    try {
      const res = await objectivesAPI.analyzeBrand(brandName, allKeywords, sector, profileType);
      setAnalysis(res.data.analysis);
      setRecommendations(res.data.recommendations || []);

      // Pre-select recommended objectives
      const preselected = {};
      const targets = {};
      res.data.recommendations.forEach((rec) => {
        if (rec.is_recommended) {
          preselected[rec.objective_type] = true;
        }
        targets[rec.objective_type] = rec.target_value;
      });
      setSelectedObjectives(preselected);
      setCustomTargets(targets);

      // Fetch enriched mission definitions with reasons
      await fetchMissionDefinitions();

      setActiveStep(2);
    } catch (err) {
      console.error('Analysis error:', err);
      setError('Erreur lors du chargement des objectifs. Veuillez réessayer.');
    } finally {
      setAnalyzing(false);
    }
  };

  // Toggle mission selection
  const handleToggleMission = (key) => {
    setSelectedMissions((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      // Ensure at least 1 mission stays selected
      const activeCount = Object.values(next).filter(Boolean).length;
      if (activeCount === 0) return prev;
      return next;
    });
  };

  const activeMissionCount = Object.values(selectedMissions).filter(Boolean).length;

  // Get first name from brand name (for advisor bubble)
  const firstName = brandName.split(' ')[0] || 'vous';
  const profileLabel = PROFILE_TYPES.find(p => p.id === profileType)?.name?.toLowerCase() || profileType;

  // Create brand with missions AND scrape data
  const handleCreate = async () => {
    if (creating || scraping) return;

    setCreating(true);
    setError(null);

    let brandId = null;

    try {
      // Step 1: Create brand
      const allKeywords = keywords.length > 0 ? keywords : [brandName];
      if (!allKeywords.includes(brandName)) {
        allKeywords.unshift(brandName);
      }

      const brandRes = await monitoringAPI.createBrand(brandName, allKeywords, sector, profileType);
      brandId = brandRes.data?.id;

      if (!brandId) {
        throw new Error('La marque n\'a pas été créée correctement');
      }

      // Step 1b: Save profile_type via updateBrandProfile
      try {
        await monitoringAPI.updateBrandProfile(brandId, {
          profile_type: profileType,
          sector: sector,
        });
      } catch (profileErr) {
        console.warn('Profile type save warning (non-blocking):', profileErr);
      }

      // Step 1c: Save social URLs (if any provided)
      const cleanUrls = Object.fromEntries(
        Object.entries(socialUrls).filter(([_, v]) => v.trim())
      );
      if (Object.keys(cleanUrls).length > 0) {
        try {
          await monitoringAPI.updateSocialUrls(brandId, cleanUrls);
        } catch (err) {
          console.warn('Social URLs save warning:', err);
        }
      }

      // Step 2: Save AI summary from Perplexity analysis (if available)
      if (analysis) {
        try {
          const aiSummary = {
            entity_description: analysis.context?.description || '',
            media_image: `Présence ${analysis.context?.presence_level || 'modérée'}. ${analysis.context?.current_sentiment || ''}`.trim(),
            estimates: analysis.estimates || {},
            context: analysis.context || {},
            generated_at: new Date().toISOString()
          };
          await monitoringAPI.updateBrandAISummary(brandId, aiSummary);
        } catch (err) {
          console.warn('AI summary save warning (non-blocking):', err);
        }
      }

      // Step 3: Save selected missions
      const activeMissions = Object.entries(selectedMissions)
        .filter(([_, active]) => active)
        .map(([key]) => key);

      if (activeMissions.length > 0) {
        try {
          await missionsAPI.setMissions(brandId, activeMissions, targetConfigs);
        } catch (missionErr) {
          console.warn('Missions creation warning:', missionErr);
          // Fallback: create objectives directly
          const objectivesToCreate = recommendations
            .filter((rec) => selectedObjectives[rec.objective_type])
            .map((rec) => {
              const targetValue = customTargets[rec.objective_type] ?? rec.target_value;
              const obj = {
                objective_type: rec.objective_type,
                target_value: typeof targetValue === 'number' ? targetValue : parseFloat(targetValue) || 0,
                baseline_value: rec.baseline_value || 0,
                horizon: rec.horizon || 'monthly',
                recommendation_reason: rec.reason || rec.recommendation_reason || '',
              };
              // Attach target_config from mission if available
              const missionKey = rec.mission_key || rec.mission;
              if (missionKey && targetConfigs[missionKey]) {
                obj.target_config = targetConfigs[missionKey];
              }
              return obj;
            })
            .filter(obj => obj.target_value > 0);

          if (objectivesToCreate.length > 0) {
            try {
              await objectivesAPI.createBulk(brandId, objectivesToCreate);
            } catch (objErr) {
              console.warn('Objectives creation fallback warning:', objErr);
            }
          }
        }
      }

      // Step 4: Create BrandPlan (auto-derived from selected missions)
      try {
        const activeMissions = Object.entries(selectedMissions)
          .filter(([_, active]) => active)
          .map(([key]) => key);
        // Pages: radar+mesurer always, comprendre+agir if 2+ missions
        const autoPages = ['radar', 'mesurer'];
        if (activeMissions.length >= 2) {
          autoPages.push('comprendre', 'agir');
        }
        // Tier: derive from mission count
        const autoTier = activeMissions.length >= 3 ? 'premium' : activeMissions.length >= 2 ? 'pro' : 'essentiel';
        await agencyAPI.setBrandPlan(brandId, {
          tier: autoTier,
          missions: activeMissions,
          pages: autoPages,
          report_frequency: 'hebdomadaire',
          has_targeted_objectives: objectifsEtendus,
        });
      } catch (err) {
        console.error('[BrandWizard] plan creation error:', err);
        // Non-blocking — brand is created, plan creation failure is not critical
      }

      // Step 5: Start scraping
      setScraping(true);
      setCreating(false);

      // Step 1/6: Auto-fetch photo from Google Images (blocking, uploads to Supabase)
      setScrapingStep({ step: 1, message: 'Recherche de photo...', total: 6 });
      try {
        await monitoringAPI.fetchBrandImages(brandId);
      } catch (_) { /* ignore - photo is optional */ }

      try {
        setScrapingStep({ step: 2, message: 'RSS + Google News (3 mois)...', total: 6 });
        setScrapingStep({ step: 3, message: 'Google News & réseaux sociaux...', total: 6 });
        setScrapingStep({ step: 4, message: 'Twitter + Perplexity...', total: 6 });
        setScrapingStep({ step: 5, message: 'Réseaux sociaux (Apify)...', total: 6 });

        const collectRes = await monitoringAPI.collectAll(brandId, {
          daysLookback: 90,
          includeRss: true,
          includeTwitter: true,
          includePerplexity: true,
          includeApify: true,
          brandUrls: Object.keys(cleanUrls).length > 0 ? cleanUrls : null,
          maxPerSource: 50,
        });
        const savedCount = collectRes.data?.total_saved || collectRes.data?.total || 0;

        setScrapingStep({ step: 6, message: `Terminé: ${savedCount} mentions sauvegardées`, total: 6 });

        // Auto-trigger Comprendre analysis (non-blocking)
        try {
          await Promise.allSettled([
            comprendreAPI.getDrivers(brandId, null, true),
            comprendreAPI.diagnose(brandId),
          ]);
        } catch (_) { /* non-blocking */ }

        await new Promise(r => setTimeout(r, 1500));

        if (onBrandCreated) {
          await onBrandCreated(brandId, brandName, allKeywords);
        }
        handleClose();
      } catch (searchErr) {
        console.warn('Scraping error:', searchErr);
        setScrapingStep({ step: 6, message: 'Scraping terminé (partiel)', total: 6 });
        await new Promise(r => setTimeout(r, 2000));

        if (onBrandCreated) {
          await onBrandCreated(brandId, brandName, allKeywords);
        }
        handleClose();
      }
    } catch (err) {
      console.error('Creation error:', err);
      setError(`Erreur: ${err.message || 'Veuillez réessayer.'}`);
      setCreating(false);
      setScraping(false);
    }
  };

  // Close and reset ALL states
  const handleClose = () => {
    setActiveStep(0);
    setBrandName('');
    setKeywords([]);
    setKeywordInput('');
    setSector('autre');
    setProfileType('organisation');
    setSocialUrls({ instagram_url: '', twitter_url: '', linkedin_url: '', facebook_url: '', youtube_url: '', tiktok_url: '', website_url: '', shop_url: '', google_maps_url: '', tripadvisor_url: '', booking_url: '', marketplace_url: '' });
    setSearchingSocials(false);
    setSocialsSearchDone(false);
    setFilledFields(new Set());
    setAnalysis(null);
    setRecommendations([]);
    setSelectedObjectives({});
    setCustomTargets({});
    setSelectedMissions({});
    setTargetConfigs({});
    setMissionDefs([]);
    setSectorSources(null);
    setObjectifsEtendus(false);
    setError(null);
    setCreating(false);
    setScraping(false);
    setScrapingStep({ step: 0, message: '', total: 0 });
    onClose();
  };

  // Go back one step
  const handleBack = () => {
    setActiveStep((prev) => {
      const next = Math.max(0, prev - 1);
      // Reset social search if going back to step 1 (name might change)
      if (next === 0) {
        setSocialsSearchDone(false);
        setFilledFields(new Set());
      }
      return next;
    });
  };

  // Determine which platforms are available based on URLs
  const availablePlatforms = PLATFORMS.map(p => ({
    ...p,
    available: p.alwaysAvailable || (p.urlKey && socialUrls[p.urlKey]?.trim()),
  }));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: '12px', minHeight: '70vh' } }}
    >
      <DialogTitle
        component="div"
        sx={{
          bgcolor: colors.charcoal,
          color: 'white',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6" component="span" sx={{ fontFamily: '"DM Sans", sans-serif', fontWeight: 600 }}>
          Nouvelle marque
        </Typography>
        <IconButton onClick={handleClose} aria-label="Fermer" sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Box sx={{ px: 3, pt: 3 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent sx={{ pt: 3, bgcolor: (activeStep === 2 || activeStep === 3) ? colors.gray50 : 'white' }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Step 1: Brand Information */}
        {activeStep === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label="Nom de la marque"
              value={brandName}
              onChange={(e) => setBrandName(e.target.value)}
              fullWidth
              required
              placeholder="Ex: Yves Koné, Orange CI, BICICI..."
              autoFocus
            />

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Mots-clés de recherche
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <TextField
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyPress={handleKeywordKeyPress}
                  placeholder="Ajouter un mot-clé..."
                  size="small"
                  fullWidth
                />
                <Button
                  variant="outlined"
                  onClick={handleAddKeyword}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  <AddIcon />
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {keywords.map((kw) => (
                  <Chip
                    key={kw}
                    label={kw}
                    onDelete={() => handleRemoveKeyword(kw)}
                    size="small"
                  />
                ))}
                {keywords.length === 0 && (
                  <Typography variant="caption" color="textSecondary">
                    Le nom de la marque sera utilisé comme mot-clé principal
                  </Typography>
                )}
              </Box>
            </Box>

            <FormControl fullWidth>
              <InputLabel>Type de profil</InputLabel>
              <Select
                value={profileType}
                onChange={(e) => setProfileType(e.target.value)}
                label="Type de profil"
              >
                {PROFILE_TYPES.map((pt) => (
                  <MenuItem key={pt.id} value={pt.id}>
                    {pt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {profileType && (
              <Typography variant="caption" color="textSecondary" sx={{ mt: -2 }}>
                {PROFILE_TYPES.find(p => p.id === profileType)?.description}
              </Typography>
            )}

            <FormControl fullWidth>
              <InputLabel>Secteur d'activité</InputLabel>
              <Select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                label="Secteur d'activité"
              >
                {SECTORS.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Step 2: Social URLs */}
        {activeStep === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Contextual message */}
            <Alert
              severity={searchingSocials ? 'info' : 'success'}
              icon={searchingSocials ? <AutoFixHighIcon sx={{
                animation: 'spin 1.5s ease-in-out infinite',
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg) scale(1)' },
                  '50%': { transform: 'rotate(180deg) scale(1.2)' },
                  '100%': { transform: 'rotate(360deg) scale(1)' },
                },
              }} /> : undefined}
              sx={{ mb: 1 }}
            >
              {searchingSocials
                ? `Recherche des profils officiels de ${brandName}...`
                : socialsSearchDone && filledFields.size > 0
                  ? `${filledFields.size} profil${filledFields.size > 1 ? 's' : ''} trouvé${filledFields.size > 1 ? 's' : ''}. Vérifiez et complétez si nécessaire.`
                  : 'Renseignez vos profils pour une collecte plus riche. Vous pourrez les ajouter plus tard dans Paramètres.'
              }
            </Alert>

            {searchingSocials && (
              <LinearProgress sx={{
                mt: -1, mb: 1, borderRadius: 2,
                '& .MuiLinearProgress-bar': {
                  bgcolor: colors.primary,
                  backgroundImage: `linear-gradient(90deg, ${colors.primary}, ${colors.mint}, ${colors.primary})`,
                  backgroundSize: '200% 100%',
                  animation: 'shimmer 1.5s ease-in-out infinite',
                  '@keyframes shimmer': {
                    '0%': { backgroundPosition: '200% 0' },
                    '100%': { backgroundPosition: '-200% 0' },
                  },
                },
              }} />
            )}

            {[
              { key: 'instagram_url', label: 'Instagram', placeholder: 'https://instagram.com/...' },
              { key: 'twitter_url', label: 'Twitter / X', placeholder: 'https://x.com/...' },
              { key: 'linkedin_url', label: 'LinkedIn', placeholder: 'https://linkedin.com/in/...' },
              { key: 'facebook_url', label: 'Facebook', placeholder: 'https://facebook.com/...' },
              { key: 'youtube_url', label: 'YouTube', placeholder: 'https://youtube.com/...' },
              { key: 'tiktok_url', label: 'TikTok', placeholder: 'https://tiktok.com/@...' },
            ].map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                value={socialUrls[field.key]}
                onChange={(e) => {
                  setSocialUrls({ ...socialUrls, [field.key]: e.target.value });
                  // Remove from filled set if user edits
                  if (filledFields.has(field.key)) {
                    setFilledFields(prev => { const n = new Set(prev); n.delete(field.key); return n; });
                  }
                }}
                fullWidth
                placeholder={field.placeholder}
                size="small"
                sx={{
                  // Subtle glow animation when field is freshly auto-filled
                  ...(filledFields.has(field.key) && {
                    '& .MuiOutlinedInput-root': {
                      animation: 'glowIn 0.6s ease-out',
                      '@keyframes glowIn': {
                        '0%': { boxShadow: `0 0 0 0 rgba(27, 94, 64, 0)`, bgcolor: 'transparent' },
                        '40%': { boxShadow: `0 0 12px 2px rgba(27, 94, 64, 0.15)`, bgcolor: 'rgba(27, 94, 64, 0.03)' },
                        '100%': { boxShadow: 'none', bgcolor: 'transparent' },
                      },
                    },
                  }),
                  // Shimmer skeleton while searching
                  ...(searchingSocials && !socialUrls[field.key] && {
                    '& .MuiOutlinedInput-root': {
                      backgroundImage: 'linear-gradient(90deg, transparent 25%, rgba(27,94,64,0.04) 50%, transparent 75%)',
                      backgroundSize: '200% 100%',
                      animation: 'fieldShimmer 1.5s ease-in-out infinite',
                      '@keyframes fieldShimmer': {
                        '0%': { backgroundPosition: '200% 0' },
                        '100%': { backgroundPosition: '-200% 0' },
                      },
                    },
                  }),
                }}
              />
            ))}

            {/* Digital Properties — conditional by profile+sector */}
            {(() => {
              const EXTRA_URL_FIELDS = [
                { key: 'website_url', label: 'Site web', placeholder: 'https://www.monsite.com', showWhen: () => true },
                { key: 'shop_url', label: 'Boutique en ligne', placeholder: 'https://shop.example.com', showWhen: (p, s) => ['artiste', 'createur'].includes(p) || ['mode', 'distribution'].includes(s) },
                { key: 'google_maps_url', label: 'Fiche Google Maps', placeholder: 'https://maps.google.com/...', showWhen: (p, s) => p === 'organisation' || ['hospitality', 'distribution', 'sante'].includes(s) },
                { key: 'tripadvisor_url', label: 'TripAdvisor', placeholder: 'https://www.tripadvisor.com/...', showWhen: (_, s) => s === 'hospitality' },
                { key: 'booking_url', label: 'Booking.com', placeholder: 'https://www.booking.com/hotel/...', showWhen: (_, s) => s === 'hospitality' },
                { key: 'marketplace_url', label: 'Marketplace (Amazon, Jumia, Etsy...)', placeholder: 'https://www.etsy.com/shop/...', showWhen: (p, s) => ['artiste', 'createur'].includes(p) || ['mode', 'distribution'].includes(s) },
              ];
              const visible = EXTRA_URL_FIELDS.filter(f => f.showWhen(profileType, sector));
              if (visible.length === 0) return null;
              return (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                    Propriétés digitales
                  </Typography>
                  <Alert severity="info" sx={{ py: 0.5 }}>
                    Renseignez vos propriétés web pour que l'IA ne recommande pas de créer ce qui existe déjà.
                  </Alert>
                  {visible.map(f => (
                    <TextField
                      key={f.key}
                      label={f.label}
                      value={socialUrls[f.key]}
                      onChange={(e) => setSocialUrls({ ...socialUrls, [f.key]: e.target.value })}
                      fullWidth
                      placeholder={f.placeholder}
                      size="small"
                    />
                  ))}
                </>
              );
            })()}
          </Box>
        )}

        {/* Step 3: Missions — RP Advisor */}
        {activeStep === 2 && !scraping && (
          <Box>
            {/* Advisor bubble */}
            <Paper
              elevation={0}
              sx={{
                bgcolor: colors.mint,
                borderRadius: '12px',
                p: 2.5,
                mb: 3,
                border: `1px solid ${colors.gray200}`,
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.94rem',
                  color: colors.charcoal,
                  lineHeight: 1.6,
                }}
              >
                <strong>{firstName}</strong>, en tant que <strong>{profileLabel}</strong>,
                voici les missions que je recommande pour protéger et développer votre réputation :
              </Typography>
            </Paper>

            {/* Objectifs étendus toggle */}
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                borderRadius: '20px',
                border: `1px solid ${objectifsEtendus ? colors.primary : (colors.borderLight || colors.gray200)}`,
                bgcolor: objectifsEtendus ? 'rgba(27, 94, 64, 0.04)' : 'white',
                mb: 3,
                transition: 'all 0.2s ease',
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={objectifsEtendus}
                    onChange={(e) => setObjectifsEtendus(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        color: colors.charcoal,
                      }}
                    >
                      Activer les objectifs étendus
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: '0.75rem',
                        color: colors.textMed,
                      }}
                    >
                      Personnalisation avancée des objectifs par mission (+300 €/mois)
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', ml: 0, gap: 1 }}
              />
            </Paper>

            {/* Mission Cards */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {(missionDefs.length > 0 ? missionDefs : []).map((def) => {
                const isActive = !!selectedMissions[def.key];
                const isRecommended = def.is_recommended;
                return (
                  <React.Fragment key={def.key}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      border: isActive ? `2px solid ${colors.primary}` : `1px solid ${colors.gray200}`,
                      borderRadius: '12px',
                      bgcolor: 'white',
                      opacity: isActive ? 1 : 0.7,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      boxShadow: isActive ? '0 4px 6px rgba(0,0,0,0.07)' : 'none',
                      '&:hover': {
                        borderColor: isActive ? colors.primary : colors.gray400,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.07)',
                        opacity: 1,
                      },
                    }}
                    onClick={() => handleToggleMission(def.key)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography
                          sx={{
                            fontFamily: '"DM Sans", sans-serif',
                            fontWeight: 600,
                            fontSize: '1rem',
                            color: colors.charcoal,
                            textTransform: 'uppercase',
                            letterSpacing: '0.02em',
                          }}
                        >
                          {def.name}
                        </Typography>
                      </Box>
                      <Chip
                        icon={isActive ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : undefined}
                        label={isRecommended ? (isActive ? 'Recommandé' : 'Recommandé') : (isActive ? 'Sélectionné' : 'Optionnel')}
                        size="small"
                        sx={{
                          borderRadius: '9999px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          px: 0.5,
                          height: 24,
                          bgcolor: isRecommended
                            ? 'rgba(46,158,107,0.1)'
                            : isActive ? 'rgba(46,158,107,0.1)' : colors.gray100,
                          color: isRecommended
                            ? colors.primary
                            : isActive ? colors.primary : colors.gray600,
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ color: colors.gray600, mb: 1.5, fontFamily: '"DM Sans", sans-serif' }}
                    >
                      {def.tagline}
                    </Typography>

                    {/* Reasons from objective_recommender.py */}
                    {def.reasons && def.reasons.length > 0 && (
                      <Box sx={{ borderTop: `1px solid ${colors.gray200}`, pt: 1.5 }}>
                        {def.reasons.map((reason, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              fontFamily: '"DM Sans", sans-serif',
                              fontSize: '0.81rem',
                              color: colors.gray600,
                              mb: 0.5,
                              pl: 1.5,
                              position: 'relative',
                              '&::before': {
                                content: '"•"',
                                position: 'absolute',
                                left: 0,
                                color: colors.primary,
                                fontWeight: 700,
                              },
                            }}
                          >
                            {reason}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Paper>
                  {isActive && objectifsEtendus && (
                    <Collapse in={isActive} timeout={350}>
                      {def.key === 'protect' && (
                        <TargetProteger
                          config={targetConfigs.protect || {}}
                          onChange={(cfg) => setTargetConfigs(prev => ({ ...prev, protect: cfg }))}
                          missionColor={colors.primary}
                        />
                      )}
                      {def.key === 'trust' && (
                        <TargetConfiance
                          config={targetConfigs.trust || {}}
                          onChange={(cfg) => setTargetConfigs(prev => ({ ...prev, trust: cfg }))}
                          missionColor={colors.primary}
                        />
                      )}
                      {def.key === 'visibility' && (
                        <TargetRayonner
                          config={targetConfigs.visibility || {}}
                          onChange={(cfg) => setTargetConfigs(prev => ({ ...prev, visibility: cfg }))}
                          missionColor={colors.primary}
                        />
                      )}
                      {def.key === 'community' && (
                        <TargetFederer
                          config={targetConfigs.community || {}}
                          onChange={(cfg) => setTargetConfigs(prev => ({ ...prev, community: cfg }))}
                          missionColor={colors.primary}
                        />
                      )}
                    </Collapse>
                  )}
                  </React.Fragment>
                );
              })}
            </Box>

            {/* Platforms monitored */}
            <Box sx={{ mt: 3 }}>
              <Typography
                variant="subtitle2"
                sx={{
                  fontFamily: '"DM Sans", sans-serif',
                  fontSize: '0.81rem',
                  color: colors.gray600,
                  mb: 1.5,
                  fontWeight: 500,
                }}
              >
                Plateformes surveillées
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availablePlatforms.map((p) => (
                  <Chip
                    key={p.key}
                    icon={p.available
                      ? <CheckIcon sx={{ fontSize: 14 }} />
                      : <BlockIcon sx={{ fontSize: 14 }} />
                    }
                    label={p.label}
                    size="small"
                    sx={{
                      borderRadius: '9999px',
                      fontSize: '0.72rem',
                      fontWeight: 500,
                      bgcolor: p.available ? 'rgba(46,158,107,0.1)' : colors.gray100,
                      color: p.available ? colors.primary : colors.gray400,
                      textDecoration: p.available ? 'none' : 'line-through',
                      '& .MuiChip-icon': {
                        color: p.available ? colors.primary : colors.gray400,
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>

            {/* Sector sources */}
            {sectorSources && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.81rem',
                    color: colors.gray600,
                    mb: 0.5,
                    fontWeight: 500,
                  }}
                >
                  Sources média de votre secteur
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: '"DM Sans", sans-serif',
                    fontSize: '0.81rem',
                    color: colors.gray500,
                  }}
                >
                  {sectorSources.label} : {sectorSources.rss_sources?.slice(0, 5).join(', ')}
                  {sectorSources.rss_sources?.length > 5 && ` (+${sectorSources.rss_sources.length - 5})`}
                </Typography>
              </Box>
            )}

            {/* Mission count */}
            <Typography
              sx={{
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 600,
                fontSize: '0.85rem',
                color: colors.primary,
                textAlign: 'center',
                mt: 3,
              }}
            >
              {activeMissionCount} mission{activeMissionCount > 1 ? 's' : ''} sélectionnée{activeMissionCount > 1 ? 's' : ''}
            </Typography>
          </Box>
        )}


        {/* Scraping loading state */}
        {scraping && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 6,
            }}
          >
            <CircularProgress sx={{ color: colors.charcoal, mb: 2 }} size={48} />
            <Typography variant="h6" sx={{ mb: 1, color: colors.charcoal, fontFamily: '"DM Sans", sans-serif' }}>
              Collecte des données
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {scrapingStep.message}
            </Typography>
            <Box sx={{ width: '80%' }}>
              <LinearProgress
                variant="determinate"
                value={(scrapingStep.step / scrapingStep.total) * 100}
                sx={{ height: 8, borderRadius: 4, '& .MuiLinearProgress-bar': { bgcolor: colors.charcoal } }}
              />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
              Cette opération peut prendre 2-3 minutes
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2 }}>
        {scraping ? (
          <Typography variant="body2" color="textSecondary">
            Veuillez patienter...
          </Typography>
        ) : activeStep === 0 ? (
          <>
            <Button onClick={handleClose} color="inherit">
              Annuler
            </Button>
            <Button
              variant="contained"
              onClick={() => setActiveStep(1)}
              disabled={!brandName.trim()}
              sx={{
                bgcolor: colors.charcoal,
                '&:hover': { bgcolor: colors.slate },
              }}
            >
              Suivant
            </Button>
          </>
        ) : activeStep === 1 ? (
          <>
            <Button onClick={handleBack} color="inherit">
              Retour
            </Button>
            <Button
              onClick={() => {
                handleAnalyze();
              }}
              color="inherit"
              disabled={analyzing}
            >
              Passer cette étape
            </Button>
            <Button
              variant="contained"
              onClick={handleAnalyze}
              disabled={analyzing}
              sx={{
                bgcolor: colors.charcoal,
                '&:hover': { bgcolor: colors.slate },
              }}
            >
              {analyzing ? 'Analyse...' : 'Suivant'}
            </Button>
          </>
        ) : activeStep === 2 ? (
          <>
            <Button onClick={handleBack} color="inherit" disabled={creating}>
              Retour
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={creating || activeMissionCount === 0}
              startIcon={creating ? <CircularProgress size={16} color="inherit" /> : <CheckCircleIcon />}
              sx={{
                bgcolor: colors.primary,
                borderRadius: '100px',
                textTransform: 'none',
                fontFamily: '"DM Sans", sans-serif',
                fontWeight: 600,
                '&:hover': { bgcolor: colors.primaryDark },
              }}
            >
              {creating ? 'Création...' : 'Créer la marque'}
            </Button>
          </>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export default BrandWizard;
